<template>
	<div class="mini-goods">
		<div class="page-head">
			<div class="tab-bar">
				<button
					v-for="item in tabs"
					:key="item.name"
					type="button"
					class="tab-btn"
					:class="{ 'is-active': tab === item.name }"
					@click="switchTab(item.name)"
				>
					{{ item.label }}
					<em v-if="badgeText(item.name) !== ''" class="tab-badge">{{ badgeText(item.name) }}</em>
				</button>
			</div>
			<el-form :inline="true" size="small" class="filter-form" @submit.native.prevent="onSearch">
				<el-form-item>
					<el-input
						v-model="keyword"
						clearable
						placeholder="名称 / 款号"
						class="keyword-input"
						@keyup.enter.native="onSearch"
					/>
				</el-form-item>
				<el-form-item>
					<el-select
						v-model="cid"
						clearable
						placeholder="大类"
						class="cid-select"
						@change="onCidChange"
					>
						<el-option
							v-for="item in cidOptions"
							:key="item.value"
							:label="item.label"
							:value="item.value"
						/>
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-select
						v-model="discountSort"
						clearable
						placeholder="折扣排序"
						class="sort-select"
						@change="onDiscountSort"
					>
						<el-option label="折扣从低到高" value="asc" />
						<el-option label="折扣从高到低" value="desc" />
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-select
						v-model="stockSort"
						clearable
						placeholder="库存数量排序"
						class="sort-select"
						@change="onStockSort"
					>
						<el-option label="库存从多到少" value="desc" />
						<el-option label="库存从少到多" value="asc" />
					</el-select>
				</el-form-item>
				<el-form-item>
					<el-button type="primary" @click="onSearch">查询</el-button>
					<el-button @click="onReset">重置</el-button>
					<el-button
						v-if="tab === 'syncable'"
						type="primary"
						:disabled="!selected.length"
						:loading="batching"
						@click="onBatchSync"
					>批量同步{{ selected.length ? '（' + selected.length + '）' : '' }}</el-button>
					<el-button
						v-if="showBatchUnsync"
						type="danger"
						plain
						:disabled="!selected.length"
						:loading="batching"
						@click="onBatchUnsync"
					>批量取消同步{{ selected.length ? '（' + selected.length + '）' : '' }}</el-button>
				</el-form-item>
			</el-form>
		</div>

		<div class="table-wrap">
			<el-table
				:key="tab"
				:data="list"
				size="small"
				height="100%"
				v-loading="loading"
				class="goods-table"
				@selection-change="onSelect"
			>
				<el-table-column v-if="canSelect" type="selection" width="48" align="center" />
				<el-table-column label="商品" min-width="280">
					<template slot-scope="scope">
						<div class="goods-cell">
							<div class="thumb">
								<img v-if="scope.row.pic" :src="scope.row.pic" alt="" width="48" height="48">
								<i v-else class="el-icon-picture-outline"></i>
							</div>
							<div class="goods-meta">
								<div class="goods-name" :title="scope.row.name">
									{{ scope.row.name || '（名称缺失）' }}
									<el-tag v-if="scope.row.name_missing" size="mini" type="warning">名称缺失</el-tag>
								</div>
								<div class="goods-sub">{{ scope.row.style_number || '-' }}</div>
							</div>
						</div>
					</template>
				</el-table-column>
				<el-table-column label="吊牌价" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.price_yuan || '-' }}</template>
				</el-table-column>
				<el-table-column label="大类" width="80" align="center">
					<template slot-scope="scope">{{ scope.row.cid_text || '-' }}</template>
				</el-table-column>
				<el-table-column v-if="tab === 'syncable'" label="渠道数" width="90" align="center">
					<template slot-scope="scope">{{ scope.row.channel_count || 0 }}</template>
				</el-table-column>
				<el-table-column v-if="tab === 'syncable'" label="最低折扣" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.min_discount_text || '-' }}</template>
				</el-table-column>
				<el-table-column v-if="tab === 'synced'" label="可售尺码" width="110" align="center">
					<template slot-scope="scope">{{ scope.row.sizes_sellable || 0 }}/{{ scope.row.sizes_total || 0 }}</template>
				</el-table-column>
				<el-table-column v-if="tab === 'syncable' || tab === 'synced'" label="可售库存" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.stock_total || 0 }}</template>
				</el-table-column>
				<el-table-column v-if="tab === 'synced'" label="C 端价" width="120" align="center">
					<template slot-scope="scope">{{ retailStart(scope.row) }}</template>
				</el-table-column>
				<el-table-column v-if="tab === 'unsellable'" label="原因" min-width="160">
					<template slot-scope="scope">
						<span :class="{ 'reason-link': scope.row.reason === '规则未配置' }" @click="onReasonClick(scope.row)">{{ scope.row.reason || '-' }}</span>
					</template>
				</el-table-column>
				<el-table-column label="中国码" width="90" align="center">
					<template slot-scope="scope">
						<span :class="{ 'warn-text': showSizeChFill(scope.row) }">{{ sizeChText(scope.row) }}</span>
					</template>
				</el-table-column>
				<el-table-column label="操作" width="240" align="center" header-align="center">
					<template slot-scope="scope">
						<span v-if="tab === 'syncable'" class="cell-link" @click="onSync(scope.row)">同步</span>
						<span v-if="tab !== 'syncable'" class="cell-link" @click="onUnsync(scope.row)">取消同步</span>
						<span v-if="showSizeChFill(scope.row)" class="cell-link cell-link-gap" @click="openSizeFill(scope.row)">补中国码</span>
						<span class="cell-link cell-link-gap" @click="openDetail(scope.row)">尺码表</span>
					</template>
				</el-table-column>
			</el-table>
		</div>

		<div class="pager">
			<el-pagination
				background
				layout="total, sizes, prev, pager, next"
				:total="total"
				:current-page="page"
				:page-size="limit"
				:page-sizes="[20, 30, 50, 100, 200]"
				@current-change="onPageChange"
				@size-change="onSizeChange"
			/>
		</div>

		<el-dialog title="尺码表" :visible.sync="detailVisible" width="980px" append-to-body @close="detail = null">
			<div v-if="detail" class="detail-head">
				<span>{{ detail.product && (detail.product.name || '（名称缺失）') }}</span>
				<span class="detail-sub">{{ detail.product && detail.product.style_number }}</span>
				<span>吊牌价 {{ (detail.product && detail.product.price_yuan) || '-' }}</span>
				<span>可售 {{ detail.sizes_sellable || 0 }}/{{ detail.sizes_total || 0 }}</span>
				<span v-if="detail.min_price_yuan">C 端价 {{ detail.min_price_yuan }} 起</span>
				<span v-if="detail.reason" class="detail-reason">{{ detail.reason }}</span>
			</div>
			<el-table v-if="detail" :data="detail.sizes || []" size="small" border max-height="420">
				<el-table-column prop="size" label="尺码" width="80" align="center" />
				<el-table-column label="中国码" width="90" align="center">
					<template slot-scope="scope">{{ scope.row.size_ch || '-' }}</template>
				</el-table-column>
				<el-table-column prop="shop_name" label="渠道" min-width="120" show-overflow-tooltip>
					<template slot-scope="scope">{{ scope.row.shop_name || '-' }}</template>
				</el-table-column>
				<el-table-column label="库存" width="70" align="center">
					<template slot-scope="scope">{{ scope.row.stock }}</template>
				</el-table-column>
				<el-table-column label="进货折扣" width="90" align="center">
					<template slot-scope="scope">{{ scope.row.discount ? (scope.row.discount + '折') : '-' }}</template>
				</el-table-column>
				<el-table-column label="进货价" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.agent_price_yuan || '-' }}</template>
				</el-table-column>
				<el-table-column label="安全库存" width="80" align="center" prop="safety_stock" />
				<el-table-column label="安全折扣" width="90" align="center">
					<template slot-scope="scope">{{ scope.row.safety_discount ? (scope.row.safety_discount + '折') : '-' }}</template>
				</el-table-column>
				<el-table-column label="是否通过" width="90" align="center">
					<template slot-scope="scope">
						<span :class="scope.row.passed ? 'ok-text' : 'gray-text'">{{ scope.row.passed ? '通过' : '未通过' }}</span>
					</template>
				</el-table-column>
				<el-table-column label="C 端价" width="100" align="center">
					<template slot-scope="scope">{{ scope.row.retail_price_yuan || '-' }}</template>
				</el-table-column>
			</el-table>
		</el-dialog>

		<el-dialog title="补中国码" :visible.sync="fillVisible" width="520px" append-to-body>
			<el-table :data="fillRows" size="small" border max-height="360">
				<el-table-column prop="size" label="尺码" width="90" align="center" />
				<el-table-column label="中国码">
					<template slot-scope="scope">
						<el-input v-model="scope.row.size_ch" size="mini" maxlength="50" />
					</template>
				</el-table-column>
			</el-table>
			<span slot="footer">
				<el-button size="small" @click="fillVisible = false">取消</el-button>
				<el-button type="primary" size="small" :loading="filling" @click="onFillSubmit">保存</el-button>
			</span>
		</el-dialog>
	</div>
</template>

<script>
	import {
		SHOP_TYPE_MINIPROGRAM,
		MINIPROGRAM_API,
		MINI_GOODS_API,
		MINI_GOODS_TABS,
		MINI_GOODS_CIDS,
		MINI_GOODS_CID_SHOE,
		getApiErrorMessage,
		pickMinIdMiniShop,
		formatMiniGoodsBadge,
		canUseMiniprogram,
	} from '@/constants/retailPlatform'

	export default {
		name: 'MiniprogramGoods',
		inject: ['appdata'],
		data() {
			return {
				tabs: MINI_GOODS_TABS,
				cidOptions: MINI_GOODS_CIDS,
				tab: 'syncable',
				keyword: '',
				cid: '',
				discountSort: '',
				stockSort: '',
				page: 1,
				limit: 20,
				total: 0,
				counts: {},
				list: [],
				selected: [],
				loading: false,
				batching: false,
				detailVisible: false,
				detail: null,
				fillVisible: false,
				fillProductId: 0,
				fillRows: [],
				filling: false,
				shop: null,
			}
		},
		computed: {
			canSelect() {
				return this.tab === 'syncable' || this.tab === 'synced' || this.tab === 'unsellable'
			},
			showBatchUnsync() {
				return this.tab === 'synced' || this.tab === 'unsellable'
			},
		},
		mounted() {
			this.loadShop()
			this.loadList()
		},
		methods: {
			badgeText(name) {
				return formatMiniGoodsBadge(this.counts[name])
			},
			retailStart(row) {
				if (row && row.min_price_yuan) {
					return row.min_price_yuan + ' 起'
				}
				return '-'
			},
			isShoe(row) {
				return row && Number(row.cid) === MINI_GOODS_CID_SHOE
			},
			showSizeChFill(row) {
				return this.isShoe(row) && Number(row.ch_missing) > 0
			},
			sizeChText(row) {
				if (!this.isShoe(row)) {
					return '-'
				}
				if (Number(row.ch_missing) > 0) {
					return '缺 ' + row.ch_missing
				}
				return '齐全'
			},
			sinkZeroStock(list) {
				if (this.tab !== 'syncable' || !list || !list.length) {
					return list || []
				}
				const has = []
				const zero = []
				for (let i = 0; i < list.length; i++) {
					if (Number(list[i].stock_total) > 0) {
						has.push(list[i])
					} else {
						zero.push(list[i])
					}
				}
				return has.concat(zero)
			},
			switchTab(name) {
				if (this.tab === name) {
					return
				}
				this.tab = name
				this.page = 1
				this.selected = []
				this.loadList()
			},
			onSelect(rows) {
				this.selected = rows || []
			},
			onSearch() {
				this.page = 1
				this.loadList()
			},
			onReset() {
				this.keyword = ''
				this.cid = ''
				this.discountSort = ''
				this.stockSort = ''
				this.page = 1
				this.loadList()
			},
			onCidChange() {
				this.page = 1
				this.loadList()
			},
			onDiscountSort(val) {
				if (val) {
					this.stockSort = ''
				}
				this.page = 1
				this.loadList()
			},
			onStockSort(val) {
				if (val) {
					this.discountSort = ''
				}
				this.page = 1
				this.loadList()
			},
			onPageChange(page) {
				this.page = page
				this.loadList()
			},
			onSizeChange(limit) {
				this.limit = limit
				this.page = 1
				this.loadList()
			},
			loadShop() {
				this.$axios.get(MINIPROGRAM_API.list, {
					params: {
						type: SHOP_TYPE_MINIPROGRAM,
						page: 1,
						limit: 10,
					},
				}).then((res) => {
					if (res.data && res.data.code == 0) {
						const payload = res.data.data || {}
						this.shop = pickMinIdMiniShop(payload.list || [])
					}
				}).catch((err) => {
					console.log(err)
				})
			},
			loadList() {
				this.loading = true
				const params = {
					tab: this.tab,
					keyword: String(this.keyword || '').trim(),
					page: this.page,
					limit: this.limit,
				}
				const cid = Number(this.cid)
				if (cid === 1 || cid === 2 || cid === 3) {
					params.cid = cid
				}
				if (this.discountSort) {
					params.order_by = 'discount'
					params.order_dir = this.discountSort
				} else if (this.stockSort) {
					params.order_by = 'stock'
					params.order_dir = this.stockSort
				}
				this.$axios.get(MINI_GOODS_API.list, {
					params: params,
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.list = []
						this.total = 0
						this.$message.error(getApiErrorMessage(res, '获取商品失败'))
						return
					}
					const data = res.data.data || {}
					this.list = this.sinkZeroStock(data.list || [])
					this.total = Number(data.count) || 0
					this.counts = data.counts || {}
				}).catch((err) => {
					console.log(err)
					this.list = []
					this.$message.error('获取商品失败')
				}).finally(() => {
					this.loading = false
				})
			},
			guardPermission() {
				if (!canUseMiniprogram(this.appdata && this.appdata.myinfo)) {
					this.$message.warning('未开通小程序功能，请联系管理员')
					return false
				}
				return true
			},
			onSync(row) {
				if (!this.guardPermission() || !row || !row.product_id) {
					return
				}
				this.$axios.post(MINI_GOODS_API.sync, {
					product_id: row.product_id,
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '同步失败'))
						this.loadList()
						return
					}
					const data = res.data.data || {}
					if (data.success) {
						this.$message.success('已同步')
					} else {
						this.$alert(data.reason || '同步未通过', '同步结果', { confirmButtonText: '知道了' })
					}
					this.loadList()
				}).catch((err) => {
					console.log(err)
					this.$message.error('同步失败，请稍后重试')
				})
			},
			selectedIds() {
				return (this.selected || []).map((row) => row.product_id).filter(Boolean)
			},
			onBatchUnsync() {
				if (!this.guardPermission()) {
					return
				}
				const ids = this.selectedIds()
				if (!ids.length) {
					return
				}
				if (ids.length > 50) {
					this.$message.warning('单次批量最多 50 个商品')
					return
				}
				this.$confirm('将立即从 C 端下架 ' + ids.length + ' 件，且不会自动同步回来', '批量取消同步', {
					type: 'warning',
					confirmButtonText: '确定取消',
					cancelButtonText: '再想想',
				}).then(() => {
					this.batching = true
					this.$axios.post(MINI_GOODS_API.batchUnsync, {
						product_ids: ids,
					}).then((res) => {
						if (!res.data || res.data.code != 0) {
							this.$message.error(getApiErrorMessage(res, '批量取消失败'))
							this.loadList()
							return
						}
						const data = res.data.data || {}
						const successCount = data.success_count != null ? data.success_count : ids.length
						const total = data.total != null ? data.total : ids.length
						this.$message.success('已取消同步 ' + successCount + '/' + total + ' 件')
						this.loadList()
					}).catch((err) => {
						console.log(err)
						this.$message.error('批量取消失败，请稍后重试')
					}).finally(() => {
						this.batching = false
					})
				}).catch(function() {})
			},
			onBatchSync() {
				if (!this.guardPermission()) {
					return
				}
				const ids = this.selectedIds()
				if (!ids.length) {
					return
				}
				if (ids.length > 50) {
					this.$message.warning('单次批量最多 50 个商品')
					return
				}
				this.batching = true
				this.$axios.post(MINI_GOODS_API.batchSync, {
					product_ids: ids,
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '批量同步失败'))
						this.loadList()
						return
					}
					const data = res.data.data || {}
					const fails = (data.list || []).filter((item) => !item.success)
					if (!fails.length) {
						this.$message.success('已同步 ' + (data.success_count || ids.length) + ' 个商品')
					} else {
						const lines = fails.map((item) => (item.product_id + '：' + (item.reason || '失败'))).join('\n')
						this.$alert('成功 ' + (data.success_count || 0) + '，失败 ' + (data.fail_count || fails.length) + '\n' + lines, '批量同步结果', {
							confirmButtonText: '知道了',
						})
					}
					this.loadList()
				}).catch((err) => {
					console.log(err)
					this.$message.error('批量同步失败，请稍后重试')
				}).finally(() => {
					this.batching = false
				})
			},
			onUnsync(row) {
				if (!this.guardPermission() || !row || !row.product_id) {
					return
				}
				this.$confirm('取消后 C 端立即不可见，确定取消同步？', '提示', {
					type: 'warning',
				}).then(() => {
					this.$axios.post(MINI_GOODS_API.unsync, {
						product_id: row.product_id,
					}).then((res) => {
						if (!res.data || res.data.code != 0) {
							this.$message.error(getApiErrorMessage(res, '取消失败'))
							return
						}
						this.$message.success('已取消同步')
						this.loadList()
					}).catch((err) => {
						console.log(err)
						this.$message.error('取消失败，请稍后重试')
					})
				}).catch(function() {})
			},
			onReasonClick(row) {
				if (!row || row.reason !== '规则未配置') {
					return
				}
				this.goShopConfig()
			},
			goShopConfig() {
				if (!this.shop || !this.shop.id) {
					this.$message.warning('请先完成小程序配置')
					return
				}
				sessionStorage.setItem('retailShopId', String(this.shop.id))
				sessionStorage.setItem('storeConfigPlatform', 'miniprogram')
				this.$router.push({
					path: '/StoreConfig/miniprogram/tabsConfig',
					query: { id: this.shop.id },
				})
			},
			openDetail(row) {
				if (!row || !row.product_id) {
					return
				}
				this.$axios.get(MINI_GOODS_API.detail, {
					params: { product_id: row.product_id },
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '获取尺码表失败'))
						return
					}
					this.detail = res.data.data || {}
					this.detailVisible = true
				}).catch((err) => {
					console.log(err)
					this.$message.error('获取尺码表失败')
				})
			},
			openSizeFill(row) {
				if (!this.showSizeChFill(row) || !row.product_id) {
					return
				}
				this.$axios.get(MINI_GOODS_API.detail, {
					params: { product_id: row.product_id },
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '获取尺码失败'))
						return
					}
					const data = res.data.data || {}
					this.fillProductId = row.product_id
					this.fillRows = (data.sizes || []).map((item) => ({
						size_id: item.size_id,
						size: item.size,
						size_ch: item.size_ch || '',
					}))
					this.fillVisible = true
				}).catch((err) => {
					console.log(err)
					this.$message.error('获取尺码失败')
				})
			},
			onFillSubmit() {
				const sizes = (this.fillRows || []).filter((row) => row.size_id).map((row) => ({
					size_id: row.size_id,
					size_ch: String(row.size_ch || '').trim(),
				}))
				this.filling = true
				this.$axios.post(MINI_GOODS_API.sizeChFill, {
					product_id: this.fillProductId,
					sizes: sizes,
				}).then((res) => {
					if (!res.data || res.data.code != 0) {
						this.$message.error(getApiErrorMessage(res, '保存失败'))
						return
					}
					const data = res.data.data || {}
					this.$message.success('已更新 ' + (data.updated || 0) + ' 条')
					this.fillVisible = false
					this.loadList()
				}).catch((err) => {
					console.log(err)
					this.$message.error('保存失败，请稍后重试')
				}).finally(() => {
					this.filling = false
				})
			},
		},
	}
</script>

<style scoped lang="less">
	.mini-goods {
		height: 100%;
		display: flex;
		flex-direction: column;
		background: #fff;
		padding: 12px 16px 8px;
		box-sizing: border-box;
		overflow: hidden;
	}

	.page-head {
		flex: none;
	}

	.tab-bar {
		display: flex;
		align-items: center;
		border-bottom: 1px solid #ebeef5;
		margin-bottom: 12px;
	}

	.tab-btn {
		position: relative;
		display: inline-flex;
		align-items: center;
		height: 40px;
		padding: 0 16px;
		border: 0;
		background: transparent;
		color: #606266;
		font-size: 14px;
		line-height: 1;
		cursor: pointer;
		outline: none;

		&.is-active {
			color: #ff4714;
			font-weight: 500;

			&::after {
				content: '';
				position: absolute;
				left: 16px;
				right: 16px;
				bottom: 0;
				height: 2px;
				background: #ff4714;
			}
		}
	}

	.tab-badge {
		display: inline-block;
		min-width: 18px;
		margin-left: 6px;
		padding: 0 6px;
		border-radius: 9px;
		background: #f4f4f5;
		color: #909399;
		font-size: 12px;
		font-style: normal;
		line-height: 18px;
		text-align: center;
	}

	.tab-btn.is-active .tab-badge {
		background: rgba(255, 71, 20, 0.12);
		color: #ff4714;
	}

	.filter-form {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		row-gap: 8px;
		margin-bottom: 12px;

		/deep/ .el-form-item {
			display: inline-flex;
			align-items: center;
			margin: 0 8px 0 0;
			vertical-align: middle;
		}

		/deep/ .el-form-item__content {
			display: flex;
			align-items: center;
			line-height: 32px;
		}

		/deep/ .el-input {
			width: 240px;
		}

		/deep/ .el-input__inner {
			height: 32px;
			line-height: 32px;
		}

		/deep/ .el-button {
			height: 32px;
			padding: 0 15px;
			margin-left: 0;
			margin-right: 8px;
			vertical-align: middle;
		}

		/deep/ .el-button:last-child {
			margin-right: 0;
		}

		/deep/ .sort-select {
			width: 140px;
		}

		/deep/ .sort-select .el-input {
			width: 140px;
		}

		/deep/ .cid-select {
			width: 100px;
		}

		/deep/ .cid-select .el-input {
			width: 100px;
		}
	}

	.keyword-input {
		width: 240px;
	}

	.table-wrap {
		flex: 1;
		min-height: 0;
	}

	.goods-table {
		width: 100%;

		/deep/ .el-table__body-wrapper {
			overflow-x: hidden;
		}
	}

	.goods-cell {
		display: flex;
		align-items: center;
		min-width: 0;
	}

	.thumb {
		width: 48px;
		height: 48px;
		flex: none;
		margin-right: 10px;
		border-radius: 4px;
		background: #f5f6f8;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;

		img {
			width: 48px;
			height: 48px;
			object-fit: cover;
			display: block;
		}

		i {
			color: #c0c4cc;
			font-size: 18px;
		}
	}

	/deep/ .thumb img {
		width: 48px !important;
		height: 48px !important;
		max-width: 48px;
		max-height: 48px;
		object-fit: cover;
		display: block;
	}

	.goods-meta {
		min-width: 0;
		flex: 1;
	}

	.goods-name {
		color: #303133;
		line-height: 1.4;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}

	.goods-sub {
		margin-top: 4px;
		font-size: 12px;
		color: #909399;
	}

	.cell-link {
		color: #ff4714;
		cursor: pointer;
		font-size: 13px;
	}

	.cell-link-gap {
		margin-left: 10px;
	}

	.reason-link {
		color: #f56c6c;
		cursor: pointer;
	}

	.warn-text {
		color: #e6a23c;
	}

	.pager {
		flex: none;
		padding: 10px 0 4px;
		text-align: right;
	}

	.detail-head {
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		margin-bottom: 12px;
		font-size: 13px;
		color: #606266;
	}

	.detail-sub {
		color: #909399;
	}

	.detail-reason {
		color: #f56c6c;
	}

	.ok-text {
		color: #67c23a;
	}

	.gray-text {
		color: #909399;
	}
</style>



// WEBPACK FOOTER //
// src/components/StoreConfig/miniprogram/goods.vue